// schema
import * as ISchema from "#webapp/src/__schema__";

// libraries
import * as React from "react";
import { createFragmentContainer } from "react-relay";

// internal
import { webAppSlackFragments } from "./WebAppSlack.gql";
import * as s from "./WebAppSlack.styles";
import { IContainer, IProps, IState } from "./WebAppSlackTypes";

// constants
import { paths } from "#webapp/src/constants/site.constant";

// components
import { LoginBtn } from "#webapp/src/components/login-btn";
import * as loginWrapperStyles from "#webapp/src/components/login-wrapper/LoginWrapper.styles";
import { M365WaitlistLink } from "../m365-waitlist-link";

// mutations
import { addSlackAccountV2, installSlackApp, loginUser } from "#webapp/src/mutations";

// util
import { page, PageEvents, track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { isAuthed } from "#webapp/src/util/auth.util";
import { windowLocation } from "#webapp/src/util/location.util";
import { getCurrentOrg } from "#webapp/src/util/org.util";
import { generateSlackAuthUrl } from "#webapp/src/util/third-party-oauth.util";
import { getCookie } from "@clockwise/web-commons/src/util/cookie.util";
import { getServerUrl } from "@clockwise/web-commons/src/util/post-message-common.util";

// routing
import { getSearchParams } from "#webapp/src/routing/routes.util";

// state
import { Typography } from "@clockwise/design-system";
import { Button, CircularProgress } from "@material-ui/core";

import { lastSignInDate, slackNonce } from "#webapp/src/state/local-storage";

// material-ui
import { getOnboardingFlowState } from "#webapp/src/state/onboarding";
import withStyles from "@material-ui/core/styles/withStyles";
import { DateTime } from "luxon";
import Helmet from "react-helmet";
import toast from "react-hot-toast";
import { useFeatureFlag } from "../../launch-darkly";

type PropsWithSlackDogfood = IProps & { slackDogfoodEnabled: boolean };

class WebAppSlackComponent extends React.Component<PropsWithSlackDogfood, IState> {
  constructor(props: PropsWithSlackDogfood) {
    super(props);

    const step = this.props.match.params.step;

    this.state = {
      verifyCodeError: "",
      installSlackAppError:
        !this.getInstallSlackAppCode() && step === "connect"
          ? "You are missing a Slack verification code"
          : "",
    };
  }

  ///////////////
  // Lifecycle //
  ///////////////

  public componentDidMount() {
    page(PageEvents.HOME_SLACK);

    const step = this.props.match.params.step;
    const installSlackAppCode = this.getInstallSlackAppCode();
    const installSlackAppState = this.getInstallSlackAppState();
    const redirect = `${getServerUrl()}${paths.slackLanding}/connect`;
    const nonce = slackNonce.get() || getCookie("SlackNonce");

    if (step === "start") {
      this.redirectToSlackAuth();
    } else if (installSlackAppCode) {
      // google tag manager
      const dataLayer = window && (window as any).dataLayer;
      if (dataLayer) {
        dataLayer.push({ event: "slackOAuthComplete" });
        dataLayer.push({
          event: "funnel_step",
          universal_step: "UNIQUE1",
          funnel_name: "slack_funnel",
          step_name: "slack_auth",
          step_number: "2",
        });
      }

      // check state
      if (!nonce || nonce !== installSlackAppState) {
        this.doInstallSlackAppFailure();
        return;
      }

      slackNonce.remove();

      if (isAuthed()) {
        const org = getCurrentOrg(this.props.viewer);

        if (!org) {
          return;
        }

        const input: ISchema.IAddSlackAccountMutationInput = {
          orgRelayId: org.id,
          verificationCode: installSlackAppCode,
          redirect,
        };

        addSlackAccountV2(
          this.props.relay.environment,
          input,
          this.onAddSlackAccountV2Success,
          this.onAddSlackAccountFailure,
        );
      } else {
        const input: ISchema.IInstallSlackAppMutationInput = {
          installSlackAppCode,
          redirect,
          useOAuthV2: true,
        };

        installSlackApp(
          this.props.relay.environment,
          input,
          this.onInstallSlackAppSuccess,
          this.onInstallSlackAppFailure,
        );
      }
    } else if (step === "connect" && isAuthed()) {
      this.props.router.push(paths.webApp);
    }
  }

  /////////////
  // Helpers //
  /////////////

  private maybeTrackOnboardingFromSlackAndStoreInSession = () => {
    // Check if user has completed onboarding yet
    const onboardingState = this.props.viewer && getOnboardingFlowState(this.props.viewer.user);
    const currentOnboardingState = onboardingState?.current?.state;
    track(TrackingEvents.APP_SLACK.STARTED_ONBOARDING_FROM_SLACK, {
      onboardingStep: currentOnboardingState,
    });
    sessionStorage.setItem("onboardingExternalAppSource", "slack");
  };

  private trackSlackComplete = () => {
    // google tag manager
    const dataLayer = window && (window as any).dataLayer;
    if (dataLayer) {
      dataLayer.push({ event: "slackConnectedToClockwise" });
    }
  };

  private getInstallSlackAppCode = () => {
    const installSlackAppSearchCodeParam = "code";
    const searchParams = getSearchParams(this.props.match);
    return searchParams.has(installSlackAppSearchCodeParam)
      ? searchParams.get(installSlackAppSearchCodeParam)
      : "";
  };

  private getInstallSlackAppState = () => {
    const installSlackAppSearchStateParam = "state";
    const searchParams = getSearchParams(this.props.match);
    return searchParams.has(installSlackAppSearchStateParam)
      ? searchParams.get(installSlackAppSearchStateParam)
      : "";
  };

  //////////////
  // Handlers //
  //////////////

  private onClickAddToSlack = (e: any) => {
    e.preventDefault();
    track(TrackingEvents.APP_SLACK.ADD_TO_SLACK);
    this.redirectToSlackAuth();
  };

  private redirectToSlackAuth = () => {
    windowLocation.assign(
      "WebAppSlackRedirect",
      generateSlackAuthUrl("slack", this.props.slackDogfoodEnabled),
    );
  };

  private onAddSlackAccountV2Success = (_payload: ISchema.IAddSlackAccountV2MutationPayload) => {
    this.onAddSlackAccountSuccessInner();
  };

  private onAddSlackAccountSuccessInner = () => {
    this.maybeTrackOnboardingFromSlackAndStoreInSession();
    track(TrackingEvents.APP_SLACK.SUCCESSFUL_INSTALL);
    track(TrackingEvents.APP_SLACK.SUCCESSFUL_CONNECT);

    this.trackSlackComplete();

    this.props.router.push(paths.webApp);
  };

  private onAddSlackAccountFailure = () => {
    track(TrackingEvents.APP_SLACK.FAIL_INSTALL);
    this.doInstallSlackAppFailure();
  };

  private onInstallSlackAppSuccess = (payload: ISchema.IInstallSlackAppMutationPayload) => {
    if (payload.installSlackAppResult) {
      this.maybeTrackOnboardingFromSlackAndStoreInSession();

      track(TrackingEvents.APP_SLACK.SUCCESSFUL_INSTALL);
      toast.success("Success! The Clockwise app is now installed in your Slack.");
      this.props.router.replace("/slack/connect");
    } else {
      track(TrackingEvents.APP_SLACK.FAIL_INSTALL);
      this.doInstallSlackAppFailure();
    }
  };

  private onInstallSlackAppFailure = (_errors: any) => {
    track(TrackingEvents.APP_SLACK.FAIL_INSTALL);
    this.doInstallSlackAppFailure();
  };

  private doInstallSlackAppFailure() {
    this.props.router.replace("/slack");
    toast.error("Sorry, there was a problem installing the Clockwise Slack app. Please try again.");
  }

  private onLoginBtnSuccess = () => {
    track(TrackingEvents.APP_SLACK.SUCCESSFUL_CONNECT);
    lastSignInDate.set(DateTime.now().toISO());
    this.trackSlackComplete();
    this.props.router.push(paths.slackLanding);
  };

  ///////////////
  // Rendering //
  ///////////////

  private renderError() {
    return (
      <React.Fragment>
        <Typography variant="h6">Sorry, there was a problem</Typography>
        <Typography variant="body1" className={this.props.classes.errorMessage}>
          {this.state.verifyCodeError || this.state.installSlackAppError}
        </Typography>
        <Button
          className={this.props.classes.topMargin}
          href={paths.slackLanding}
          color="primary"
          variant="contained"
        >
          Try Again
        </Button>
      </React.Fragment>
    );
  }

  private renderDisclaimer() {
    return (
      <Typography
        className={`${this.props.classes.caption} ${this.props.classes.topMargin}`}
        variant="caption"
      >
        By signing in, you agree to Clockwise's{" "}
        <a
          className={this.props.classes.anchor}
          href={paths.terms}
          target="_blank"
          rel="noreferrer"
        >
          terms of service
        </a>{" "}
        and{" "}
        <a
          className={this.props.classes.anchor}
          href={paths.privacy}
          target="_blank"
          rel="noreferrer"
        >
          privacy policy
        </a>
        .
      </Typography>
    );
  }

  private renderStartRedirect() {
    return (
      <React.Fragment>
        <Typography variant="h6">Welcome to Clockwise!</Typography>
        <Typography className={this.props.classes.topMargin} variant="body2">
          One moment while we redirect to Slack...
        </Typography>
      </React.Fragment>
    );
  }

  private renderLandingCopy() {
    const installCode = this.getInstallSlackAppCode();

    if (!!installCode && !this.state.installSlackAppError) {
      return <div />;
    } else if (this.state.installSlackAppError) {
      return this.renderError();
    }

    return (
      <React.Fragment>
        <Typography variant="h6">Clockwise for Slack</Typography>
        <a className={this.props.classes.topMargin} href="" onClick={this.onClickAddToSlack}>
          <img
            alt="Add to Slack"
            height="40"
            width="139"
            src="https://platform.slack-edge.com/img/add_to_slack@2x.png"
          />
        </a>
      </React.Fragment>
    );
  }

  private renderConnectCalendar() {
    return (
      <React.Fragment>
        <Typography variant="h6">Sign in to setup Slack application</Typography>
        <div className="cw-mt-8 cw-flex cw-flex-col cw-items-center">
          <LoginBtn
            hideSnackBarMessages
            style={loginWrapperStyles.signupBtn.base}
            labelStyle={loginWrapperStyles.signupBtn.label}
            loginUserMutation={loginUser}
            label="Sign in with Google"
            onSuccess={this.onLoginBtnSuccess}
            preventLoginRedirect={true}
          />
          <M365WaitlistLink />
        </div>
        {this.renderDisclaimer()}
      </React.Fragment>
    );
  }

  public render() {
    const step = this.props.match.params.step;
    const installCode = this.getInstallSlackAppCode();

    return (
      <div className={this.props.classes.wrapper}>
        <Helmet>
          <title>Clockwise for Slack · Clockwise</title>
        </Helmet>
        <div className={this.props.classes.container}>
          {!isAuthed() ? (
            this.renderConnectCalendar()
          ) : installCode ? (
            <CircularProgress />
          ) : step === "start" ? (
            this.renderStartRedirect()
          ) : step === "connect" ? (
            this.renderConnectCalendar()
          ) : (
            this.renderLandingCopy()
          )}
        </div>
      </div>
    );
  }
}

const WebAppSlackWithDogfoodEnabled: React.FC<IProps> = (props) => {
  const [slackDogfoodEnabled] = useFeatureFlag("SlackDogfoodApp");

  return <WebAppSlackComponent {...props} slackDogfoodEnabled={slackDogfoodEnabled} />;
};

export const WebAppSlackStyled = withStyles(s.styles)(WebAppSlackWithDogfoodEnabled);

export const WebAppSlack = createFragmentContainer<IContainer>(
  WebAppSlackStyled,
  webAppSlackFragments,
);
